import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Folder,
    MessageSquare,
    Search,
    BookOpen,
    Trash2,
    ArrowRight,
    Calendar,
    Car
} from 'lucide-react';
import { api } from '../lib/api';

export default function Projects() {
    const navigate = useNavigate();
    const [projects, setProjects] = useState<any[]>([]);

    useEffect(() => {
        const load = async () => {
            const data = await api.getProjects();
            setProjects(data);
        };
        load();
    }, []);

    const handleDelete = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        if (confirm('Delete this project?')) {
            await api.deleteProject(id);
            const data = await api.getProjects();
            setProjects(data);
        }
    };

    const handleProjectClick = (project: any) => {
        if (project.type === 'diagnostic') {
            navigate('/chat', { state: { resumeProject: project } });
        } else if (project.type === 'parts') {
            navigate('/parts', { state: { initialSearch: project.name, vehicleContext: project.vehicleContext } });
        } else if (project.type === 'diagrams') {
            navigate('/diagrams', { state: { initialSearch: project.name } });
        }
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'diagnostic': return <MessageSquare size={16} className="text-primary" />;
            case 'parts': return <Search size={16} className="text-secondary" />;
            case 'diagrams': return <BookOpen size={16} className="text-accent" />;
            default: return <Folder size={16} className="text-gray-400" />;
        }
    };

    return (
        <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-6xl mx-auto space-y-8">
                <div className="flex justify-between items-end">
                    <div className="space-y-2">
                        <h2 className="text-3xl font-black text-white uppercase tracking-tighter">My Projects</h2>
                        <p className="text-gray-500 text-sm">Saved diagnostic sessions and part lookups.</p>
                    </div>
                </div>

                {projects.length === 0 ? (
                    <div className="py-20 text-center space-y-4">
                        <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto text-gray-700">
                            <Folder size={32} />
                        </div>
                        <p className="text-gray-500 italic">No saved projects found on this device.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {projects.map((project) => (
                            <div
                                key={project.id}
                                onClick={() => handleProjectClick(project)}
                                className="bg-dark/40 border border-slate/20 rounded-2xl p-6 hover:border-primary/30 transition-all group cursor-pointer relative overflow-hidden"
                            >
                                {/* Background Glow */}
                                <div className="absolute -inset-1 bg-gradient-to-r from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 blur transition-opacity"></div>

                                <div className="relative space-y-4">
                                    <div className="flex justify-between items-start">
                                        <div className="p-2 bg-slate/10 rounded-lg">
                                            {getTypeIcon(project.type)}
                                        </div>
                                        <button
                                            onClick={(e) => handleDelete(e, project.id)}
                                            className="p-2 text-gray-600 hover:text-red-400 transition-colors"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>

                                    <div className="space-y-1">
                                        <h3 className="text-white font-bold group-hover:text-primary transition-colors line-clamp-1">
                                            {project.name}
                                        </h3>
                                        {project.vehicleContext && (
                                            <div className="flex items-center gap-1.5 text-[10px] text-gray-500 uppercase font-bold tracking-wider">
                                                <Car size={10} />
                                                {project.vehicleContext.year} {project.vehicleContext.make} {project.vehicleContext.model}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between pt-4 border-t border-white/5">
                                        <div className="flex items-center gap-1.5 text-[9px] text-gray-500">
                                            <Calendar size={10} />
                                            {new Date(project.updatedAt).toLocaleDateString()}
                                        </div>
                                        <ArrowRight size={14} className="text-white/20 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
